import { Form, Formik, FormikHelpers } from 'formik';
import { emailSchema } from '../../utils/formSchema';
import ActivateAccountEmailInput from '../activate-account-email-input/activate-account-email-input';
import { ActivateAccountFormInput } from '../../interfaces/interfaces';
import { useMutation } from '@apollo/client';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { VerifyEmailDocument } from '../../queries';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import styles from './activate-account.module.scss';
import { logger } from '../../utils/logging';
import { AlertTriangle } from 'react-feather';

export function MissingParametersError() {
  return (
    <>
      <Row>
        <Col>
          <p className={'header'}>We can't activate your account</p>
          <p className={'subtext'}>
            This activation link is incomplete, so we can't continue.
          </p>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className={styles['error-panel']} role={'alert'}>
            <div className={styles['error-icon']} aria-hidden={'true'}>
              <AlertTriangle size={16} strokeWidth={2} />
            </div>
            <div className={styles['error-text']}>
              Please contact SPINS support so we can send you a new activation
              link.
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
}

export function ActivateAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialValues: ActivateAccountFormInput = {
    email: '',
  };
  const clientId = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');

  const [verifyEmail] = useMutation(VerifyEmailDocument);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (
    values: ActivateAccountFormInput,
    { setErrors }: FormikHelpers<ActivateAccountFormInput>
  ) => {
    if (!clientId || !redirectUri || !executeRecaptcha) {
      setErrors({
        email: 'Invalid parameters.',
      });
      return Promise.resolve();
    }

    let captchaToken: string;
    try {
      captchaToken = await executeRecaptcha('activate_account');
    } catch {
      setErrors({
        email: 'Unexpected error.',
      });
      return;
    }

    return verifyEmail({
      variables: {
        email: values.email,
        clientId,
        redirectUri,
        captchaToken,
        registration: null,
      },
    })
      .then((_) => {
        // FIXME: What to do if the user is already activated?
        // if (res.data?.verifyEmail) {
        //   navigate(`/verify-email?${searchParams.toString()}`);
        // }
        navigate(`/verify-email?${searchParams.toString()}`, {
          state: { from: 'activate' },
        });
      })
      .catch((error) => {
        logger.error(error);
        setErrors({
          email: error instanceof Error ? error.message : 'Unexpected error.',
        });
      });
  };

  return !!clientId || !!redirectUri ? (
    <Formik
      validateOnChange={true}
      initialValues={initialValues}
      validationSchema={emailSchema}
      onSubmit={handleSubmit}
      validateOnMount={false}
    >
      <Form>
        <ActivateAccountEmailInput />
      </Form>
    </Formik>
  ) : (
    <MissingParametersError />
  );
}

export default ActivateAccount;
